import { useRef, useState } from "react";
import { toPng } from "html-to-image";
import { Download } from "lucide-react";
import { toast } from "sonner";

const MISSING = "À découvrir…";

export const CollectibleCard = ({ c, index, total }) => {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const f = { x: 0.52, y: 0.62, s: 1.75, ...(c.portraitFocus || {}) };
  const portraitStyle = {
    position: "absolute", maxWidth: "none",
    width: `${f.s * 100}%`, height: `${f.s * 100}%`,
    left: `calc(50% - ${f.x * f.s * 100}%)`, top: `calc(50% - ${f.y * f.s * 100}%)`,
  };
  const rows = [
    ["Prénom", c.name],
    ["Nom de famille", c.surname || MISSING],
    ["Espèce", c.speciesLabel || c.species],
    ["Mois de naissance", c.birthMonth || MISSING],
  ];

  const download = async () => {
    if (!cardRef.current || busy) return;
    setBusy(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        pixelRatio: 3,
        cacheBust: true,
        backgroundColor: c.bg,
        style: { transform: "none", boxShadow: "none", margin: "0" },
      });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `fiche-mellowfriend-${c.slug}.png`;
      a.click();
      toast.success(`La fiche de ${c.name} est téléchargée ✿`);
    } catch (e) {
      toast.error("Oups, impossible de créer l'image. Réessaie dans un instant.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="inline-flex flex-col items-start gap-4 w-full max-w-sm">
      <div
        ref={cardRef}
        data-testid="collectible-card"
        className="collectible-card relative w-full rounded-[1.75rem] p-[6px]"
        style={{ background: c.color }}
      >
        <div className="rounded-[1.4rem] bg-[#FFFDF9] border-2 border-dashed px-6 pt-5 pb-6 relative overflow-hidden" style={{ borderColor: c.color }}>
          <span className="daisy-deco absolute -right-3 -top-3 opacity-20" style={{ width: 90, height: 90 }} />
          <div className="flex items-center justify-between gap-3">
            <p className="font-cursive text-xl text-[#78353A]/70 leading-none">Fiche MellowFriend</p>
            <span
              data-testid="collectible-card-number"
              className="font-cursive text-lg text-[#78353A] rounded-full px-3 py-0.5 leading-none"
              style={{ background: c.color }}
            >
              N° {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
            </span>
          </div>
          <div className="mt-4 flex items-center gap-4">
            {c.portrait && (
              <div className="relative w-20 h-20 rounded-full overflow-hidden shrink-0 border-2" style={{ borderColor: c.color, background: c.bg }}>
                <img src={c.portrait} alt={c.name} crossOrigin="anonymous" className="object-cover" style={portraitStyle} data-testid="collectible-portrait" />
              </div>
            )}
            <dl className="flex-1 space-y-1.5">
              {rows.map(([k, v]) => (
                <div key={k} className="flex items-baseline gap-2 border-b border-dotted border-[#78353A]/15 pb-1">
                  <dt className="font-cursive text-lg text-[#78353A] leading-none whitespace-nowrap">{k}</dt>
                  <dd className="ml-auto text-sm text-[#4A3B32] text-right" data-testid={`collectible-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{v}</dd>
                </div>
              ))}
            </dl>
          </div>
          <p className="mt-4 text-[10px] tracking-wide text-[#78353A]/50 text-right font-cursive">By MiniMellow</p>
        </div>
      </div>
      <button
        type="button"
        onClick={download}
        disabled={busy}
        data-testid="collectible-download-btn"
        className="inline-flex items-center gap-2 rounded-full px-5 py-2 font-cursive text-xl text-[#78353A] border-2 border-dashed bg-[#FFFDF9]/70 hover:bg-[#FFFDF9] hover:-translate-y-0.5 transition-[transform,background-color] duration-300 disabled:opacity-60"
        style={{ borderColor: c.color }}
      >
        <Download className="w-4 h-4" strokeWidth={2.2} />
        {busy ? "Préparation…" : "Télécharger la fiche"}
      </button>
    </div>
  );
};

export default CollectibleCard;
